import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LuEye, LuEyeOff, LuMailCheck, LuArrowLeft } from "react-icons/lu";
import {
  loadingToggleAction,
  loginAction,
  loginFailedAction,
} from "../../store/actions/AuthActions";
import {
  setProfileData,
  setTechSkills,
  setFileResume,
  setActivities,
  setJobsApplied,
  setJobsList,
  setMembershipData,
  setApiTokenReady,
  setResetEmail,
} from "../../store/actions/actions";
import { Brand, Field, Note } from "../components/Ui";
import SEO from "../components/SEO";
import "./auth.css";

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const errorMessage = useSelector((s) => s.auth.errorMessage);
  const showLoading = useSelector((s) => s.auth.showLoading);
  const apiToken = useSelector((s) => s.profile.apiToken);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ username: "", password: "" });
  const [resending, setResending] = useState(false);
  const resendTimerRef = useRef(null);

  /* clear the pending 4s resend-result timer on unmount */
  useEffect(
    () => () => {
      if (resendTimerRef.current) clearTimeout(resendTimerRef.current);
    },
    []
  );

  /* reset any stale profile state when landing on login (same as legacy) */
  useEffect(() => {
    dispatch(setProfileData({}));
    dispatch(setTechSkills([]));
    dispatch(setFileResume(""));
    dispatch(setActivities([]));
    dispatch(setJobsApplied([]));
    dispatch(setJobsList([]));
    dispatch(setMembershipData([]));
    dispatch(setApiTokenReady(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errKind = useMemo(() => {
    const m = errorMessage || "";
    if (!m) return null;
    if (m === "Mail has been sent to your email-id. Please check your email for activation.") return "mail-sent";
    if (m === "Your account is not active. Please check your email for activation.") return "inactive";
    if (m === "User not found") return "not-found";
    if (
      m.includes("password") ||
      m.includes("incorrect") ||
      m.includes("Incorrect password") ||
      m.includes("invalid") ||
      m === "Invalid credentials"
    )
      return "bad-creds";
    if (m === "User registered successfully") return null;
    return "other";
  }, [errorMessage]);

  function onLogin(e) {
    e.preventDefault();
    /* legacy dispatched setResetEmail before validating, so the typed email
       is remembered even when the form fails validation */
    dispatch(setResetEmail(username));
    const next = { username: "", password: "" };
    let bad = false;
    if (!username) {
      next.username = "Email is required";
      bad = true;
    }
    if (!password) {
      next.password = "Password is required";
      bad = true;
    }
    setErrors(next);
    if (bad) return;
    dispatch(loadingToggleAction(true));
    dispatch(loginAction(username, password, navigate));
  }

  /* resend activation mail (same endpoint + payload as legacy, including the
     legacy 4-second delayed handling of the response) */
  async function resendActivation() {
    setResending(true);
    try {
      const response = await fetch(
        "https://us-east1-foursssolutions.cloudfunctions.net/send_activation_link_v2",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
          body: JSON.stringify({ task: "resend_activation_link", user_mail: username }),
        }
      );
      await response.json();
      resendTimerRef.current = setTimeout(() => {
        if (response.ok) {
          dispatch(
            loginFailedAction("Mail has been sent to your email-id. Please check your email for activation.")
          );
          setResending(false);
        }
        /* legacy left the loader on when the request failed — quirk kept */
      }, 4000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error verifying token:", err);
    }
  }

  return (
    <div className="csx">
      <SEO
        title="Sign in"
        description="Sign in to CareerSavvy to see your job matches, tailor your resume and practise interviews."
        canonicalPath="/login"
        breadcrumbs={[{ label: "Sign in", path: "/login" }]}
      />
      <div className="csx-page">
        <main className="auth-hero">
          <div className="auth-mesh" aria-hidden="true" />
          <div className="auth-box">
            <div className="auth-brand">
              <Brand />
            </div>

            <div className="ld-glass auth-panel stack-3">
              <div className="stack-2">
                <h1 className="auth-title">Welcome back</h1>
                <p className="t-sm">Sign in with the email you signed up with.</p>
              </div>

              {errKind === "mail-sent" ? (
                <Note tone="accent">
                  <LuMailCheck size={17} />
                  <span>Activation link sent. Check your inbox to activate your account.</span>
                </Note>
              ) : null}
              {errKind === "inactive" ? (
                <Note tone="amber">
                  <span>
                    Your account is not active yet.{" "}
                    <button
                      type="button"
                      onClick={resendActivation}
                      disabled={resending}
                      style={{ fontWeight: 600, textDecoration: "underline", color: "inherit" }}
                    >
                      {resending ? "Sending…" : "Resend activation email"}
                    </button>
                  </span>
                </Note>
              ) : null}
              {errKind === "not-found" ? (
                <Note tone="danger">
                  <span>
                    No account found for that email.{" "}
                    <Link to="/page-register" style={{ fontWeight: 600, textDecoration: "underline", color: "inherit" }}>
                      Create one free
                    </Link>
                  </span>
                </Note>
              ) : null}
              {errKind === "bad-creds" || errKind === "other" ? (
                <Note tone="danger">
                  <span>{errKind === "bad-creds" ? "Incorrect email or password. Try again." : errorMessage}</span>
                </Note>
              ) : null}

              <form className="stack-3" onSubmit={onLogin} noValidate>
                <Field label="Email" error={errors.username}>
                  <input
                    className="input"
                    type="email"
                    autoComplete="username"
                    placeholder="you@example.com"
                    value={username}
                    aria-invalid={!!errors.username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </Field>
                <Field label="Password" error={errors.password}>
                  <div className="input-wrap">
                    <input
                      className="input"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      aria-invalid={!!errors.password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{ paddingRight: 44 }}
                    />
                    <span className="input-trail">
                      <button
                        type="button"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowPassword((v) => !v)}
                      >
                        {showPassword ? <LuEyeOff size={17} /> : <LuEye size={17} />}
                      </button>
                    </span>
                  </div>
                </Field>
                <div className="row-between">
                  <span />
                  <Link
                    to="/page-forgot-password"
                    className="t-sm t-accent"
                    style={{ fontWeight: 600 }}
                    onClick={() => dispatch(setResetEmail(username))}
                  >
                    Forgot password?
                  </Link>
                </div>
                <button type="submit" className="btn btn-mint btn-lg btn-full" disabled={showLoading}>
                  {showLoading ? <span className="spinner" /> : null}
                  Sign in
                </button>
              </form>

              <p className="t-sm t-center">
                New here?{" "}
                <Link to="/page-register" className="t-accent" style={{ fontWeight: 600 }}>
                  Create an account
                </Link>
              </p>
            </div>

            <p className="t-sm t-center" style={{ marginTop: 6 }}>
              <Link
                to="/"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, opacity: 0.75, fontWeight: 600 }}
              >
                <LuArrowLeft size={15} /> Back to home
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
